import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const SERVICES = [
  {
    icon: "ri-newspaper-line",
    title: "影视资讯速递",
    desc: "持续更新电影与电视剧的定档、预告、上线信息，帮你第一时间掌握影视动态。",
  },
  {
    icon: "ri-file-text-line",
    title: "剧情介绍整理",
    desc: "用清晰的结构梳理每一部作品的剧情脉络，让你在开播前就对故事心中有数。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "专业影评分析",
    desc: "编辑部与评论作者从类型、表演、视听语言等角度出发，提供有观点的深度解读。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选找片",
    desc: "按类型、地区、年份快速筛选，配合口碑排行，让每一次观影选择都有据可依。",
  },
];

const GUIDES = [
  "顶部导航可直达首页、电影、电视剧与影评专题，移动端点击右上角菜单按钮展开导航。",
  "首页搜索框输入关键词即可跳转搜索结果页，也可在搜索页按作品名称、类型、导演或演员查找。",
  "分类页支持子类型、地区、年份筛选与最新/热门/评分排序，筛选后可按页浏览。",
  "作品详情页提供剧情简介与影评节选，点击「收藏作品」即可本地保存，刷新后仍然保留。",
];

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8ccthrh5tg6socjsr0";

export default function About() {
  usePageMeta({
    title: "关于我们 - 生活中的玛丽",
    description:
      "生活中的玛丽是一个专注电影与电视剧资讯、剧情介绍及影评分析的内容平台，了解我们的定位、服务内容、联系方式与版权声明。",
    keywords: "生活中的玛丽,关于我们,影视资讯平台,影评网站,联系我们",
  });

  const [formData, setFormData] = useState({ name: "", email: "", topic: "", message: "" });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const update = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const raw = new FormData(e.currentTarget);
    const honeypot = String(raw.get("contact_alt") || "").trim();

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus("error");
      setFormError("请完整填写姓名、邮箱与反馈内容后再提交。");
      return;
    }

    setStatus("submitting");

    if (honeypot) {
      setStatus("success");
      setFormData({ name: "", email: "", topic: "", message: "" });
      return;
    }

    const payload = new URLSearchParams();
    payload.append("name", formData.name.trim());
    payload.append("email", formData.email.trim());
    payload.append("topic", formData.topic);
    payload.append("message", formData.message.trim());

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (!ok || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setFormData({ name: "", email: "", topic: "", message: "" });
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <div className="w-full">
      <div className="bg-primary-500">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="text-background-300">
            <Breadcrumb items={[{ label: "关于我们" }]} />
          </div>
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-background-50">关于我们</h1>
          <p className="mt-3 text-sm md:text-base text-background-200 max-w-3xl leading-relaxed">
            生活中的玛丽是一个专注电影与电视剧的内容平台。我们相信，好的影视资讯不只是告诉你「有什么新片」，
            更要帮你想清楚「为什么值得看」。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12 space-y-10 md:space-y-14">
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <div className="flex items-center gap-3">
              <span className="w-1 h-6 rounded-full bg-accent-500"></span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">网站介绍</h2>
            </div>
            <div className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-7 space-y-4 text-sm md:text-base text-foreground-700 leading-loose">
              <p>
                生活中的玛丽成立于内容创作社区，最初只是几位影视爱好者分享观影笔记的地方。随着内容不断积累，
                我们逐渐形成了一个覆盖电影与电视剧资讯、剧情概览、作品分类和深度评论的综合门户。
              </p>
              <p>
                我们的目标用户包括影视剧粉丝、影视行业研究者，以及希望寻找观影参考的普通观众。
                无论你是想快速了解一部新作品，还是想读一篇有观点的长评，都能在这里找到需要的内容。
              </p>
              <p>
                目前站内展示的影视数据为前端演示内容，用于完整呈现浏览、筛选、搜索与收藏等体验流程，
                真实的动态内容将在后端接口接入后同步上线。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-1 space-y-5">
            <div className="bg-background-50 rounded-lg border border-background-200 p-5">
              <h3 className="font-heading font-bold text-base text-foreground-950 mb-4">联系方式</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="text-foreground-900 font-medium">联系邮箱</p>
                    <p className="text-foreground-500 mt-0.5">contact@marylife.com</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                    <i className="ri-hand-heart-line"></i>
                  </span>
                  <div>
                    <p className="text-foreground-900 font-medium">内容合作</p>
                    <p className="text-foreground-500 mt-0.5">欢迎投稿影评与专题策划</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-md bg-background-100 text-foreground-600 flex items-center justify-center shrink-0">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <p className="text-foreground-900 font-medium">服务时间</p>
                    <p className="text-foreground-500 mt-0.5">工作日 9:00 - 18:00</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200 p-5">
              <h3 className="font-heading font-bold text-base text-foreground-950 mb-4">使用说明</h3>
              <ol className="space-y-3 text-sm text-foreground-700 leading-relaxed list-decimal pl-4">
                {GUIDES.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ol>
            </div>
          </aside>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1 h-6 rounded-full bg-accent-500"></span>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">我们能提供什么</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (
              <div key={s.title} className="bg-background-50 rounded-lg border border-background-200 p-5">
                <span className="w-11 h-11 rounded-md bg-primary-50 text-primary-500 flex items-center justify-center mb-4">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading font-bold text-base text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="feedback" className="scroll-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-6 rounded-full bg-accent-500"></span>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">反馈与建议</h2>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                发现内容有误、想推荐一部作品，或者希望与我们合作？填写右侧表单，我们会尽快回复。
                你也可以直接发送邮件至 contact@marylife.com。
              </p>
            </div>

            <div className="lg:col-span-2">
              <form
                data-readdy-form
                id="mary-contact-form"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-7 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="mary-name" className="block text-sm font-medium text-foreground-800 mb-2">
                      姓名 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="mary-name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="请输入你的姓名"
                      className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="mary-email" className="block text-sm font-medium text-foreground-800 mb-2">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="mary-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="your@email.com"
                      className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="mary-topic" className="block text-sm font-medium text-foreground-800 mb-2">
                    反馈类型
                  </label>
                  <select
                    id="mary-topic"
                    name="topic"
                    value={formData.topic}
                    onChange={(e) => update("topic", e.target.value)}
                    className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-300 cursor-pointer"
                  >
                    <option value="">请选择反馈类型</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="作品推荐">作品推荐</option>
                    <option value="内容合作">内容合作</option>
                    <option value="其它建议">其它建议</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="mary-message" className="block text-sm font-medium text-foreground-800 mb-2">
                    反馈内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="mary-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => update("message", e.target.value.slice(0, 500))}
                    placeholder="请描述你的问题或建议（最多 500 字）"
                    className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 resize-none"
                  ></textarea>
                  <p className="mt-1.5 text-xs text-foreground-500 text-right">{formData.message.length} / 500</p>
                </div>

                <div className="form-field-alt" aria-hidden="true">
                  <label htmlFor="mary-contact-alt">请勿填写此字段</label>
                  <input
                    id="mary-contact-alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === "error" && formError && (
                  <p className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-md px-4 py-3">
                    {formError}
                  </p>
                )}
                {status === "success" && (
                  <p className="text-sm text-secondary-700 bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3">
                    提交成功，感谢你的反馈！我们会尽快与你联系。
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors cursor-pointer"
                >
                  <i className="ri-send-plane-line text-lg"></i>
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
              </form>
            </div>
          </div>
        </section>

        <section>
          <div className="bg-primary-500 rounded-lg p-6 md:p-8">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-background-50 mb-4">版权声明</h2>
            <div className="space-y-3 text-sm text-background-200 leading-relaxed">
              <p>
                本站所展示的影视作品信息、图片与评论内容仅用于信息展示与内容演示，版权归各自权利人所有。
                如相关内容涉及版权问题，请通过上方邮箱与我们联系，我们将及时处理。
              </p>
              <p>
                本站原创的影评与专题文章版权归生活中的玛丽所有，转载请注明出处并保留原文链接。
                未经授权，不得用于商业用途。
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}